import React, {
  Fragment,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Tender, TenderCategory } from '@app/models';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  FilePicker,
  Grid,
  Loader,
  Paper,
  Tabs,
  TextField,
} from '@app/components';
import {
  deleteTenderFile,
  getTenderById,
  updateTender,
  uploadTenderFiles,
} from '@app/api';
import { IconCheckCircle16, IconClear, IconSendMessage } from '@app/icons';
import { ProfileAvatar } from '../../components';
import { TenderForm, TenderFormData, useForm } from '@app/forms';
import { LayoutProfile } from '@app/layouts';
import placeholderImage from '../../../Tender/placeholder.png';
import { Replies } from '../../../../common/TenderInfo/components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@app/providers';
import { TenderFormValues } from '../../../../forms/Tender/Tender';

interface Props {}

interface TenderData extends TenderFormValues {
  filesIds: string | null;
  companyId: string | null;
  requirements?: string[];
}

function PageProfileTenderEdit(props: Props) {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const [tender, setTender] = useState<Tender | null>(null);
  const [pending, setPending] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [requirement, setRequirement] = useState<string>('');
  const [requirements, setRequirements] = useState<string[]>([]);
  const { onChange, values, errors, validate, setValues } = useForm<TenderData>(
    {
      oked: null,
      kato: null,
      name: '',
      description: '',
      deliveryDate: null,
      endDate: null,
      price: '',
      companyId: null,
      requirements: [''],
      filesIds: null,
      measure: null,
    }
  );

  const isSpecialistTender = tender?.category === TenderCategory.WORKER;

  const getData = useCallback(async () => {
    try {
      const tenderResponse = await getTenderById(id as string);

      setTender(tenderResponse);
      setError(false);
      setPending(false);
      if (tenderResponse.requirements) {
        const requirements = tenderResponse.requirements.filter(
          (el) => el.name.length > 1
        );
        setRequirements(requirements.map((elem) => elem.name));
      }
    } catch (e) {
      setError(true);
      setPending(false);
    }
  }, [id]);

  const onChangeRequirement = useCallback((value: string) => {
    setRequirement(value);
  }, []);

  const onClickAddRequirement = useCallback(() => {
    setRequirements((prevRequirements) => [...prevRequirements, requirement]);
    setRequirement('');
  }, [requirement]);

  const onClickDeleteRequirement = useCallback(
    (index: number) => () => {
      setRequirements((prevRequirements) =>
        prevRequirements.filter(
          (prevRequirement, prevRequirementIndex) =>
            prevRequirementIndex !== index
        )
      );
    },
    []
  );

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onChangeTenderForm = useCallback(
    (values: Partial<TenderFormValues>) => {
      const toUpdateData: Partial<TenderFormValues> = {
        kato: values?.kato,
        description: values.description,
        price: values.price,
        deliveryDate: values.deliveryDate,
        endDate: values.endDate,
        name: values.name,
        measure: values.measure,
      };

      if (!!values.oked) {
        toUpdateData.oked = values.oked;
      }

      setValues(toUpdateData);
    },
    [setValues]
  );

  const onChangeAvatar = useCallback(
    async (files: File) => {
      try {
        if (!tender) return;
        const response = await uploadTenderFiles(
          tender!.id,
          [files],
          'imagesIds'
        );
        setTender({ ...tender, imagesIds: response?.imagesIds });
      } catch (e) {}
    },
    [tender]
  );

  const onClickSave = useCallback(async () => {
    try {
      const hasErrors = await validate(
        yup.object().shape({
          kato: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
          name: yup.string().required(t('required', { ns: 'validation' })!),
          description: yup
            .string()
            .required(t('required', { ns: 'validation' })!),
          endDate: yup.date().required(t('required', { ns: 'validation' })!),
          deliveryDate: yup
            .date()
            .required(t('required', { ns: 'validation' })!),
          ...(isSpecialistTender
            ? {}
            : {
                price: yup
                  .string()
                  .required(t('required', { ns: 'validation' })!),
                measure: yup
                  .object()
                  .nullable()
                  .required(t('required', { ns: 'validation' })!),
                oked: yup
                  .object()
                  .nullable()
                  .required(t('required', { ns: 'validation' })!),
              }),
        })
      );

      if (hasErrors) {
        console.log('Ошибки валидации:', errors);
        showNotification({
          variant: 'error',
          message: 'Пожалуйста, заполните все обязательные поля',
        });
        return;
      }

      setPending(true);

      const toUpdateData: Partial<TenderFormData> = {
        description: values.description,
        price: values.price,
        name: values.name,
        companyId: tender?.companyId,
      };

      if (values?.measure) {
        toUpdateData.measure = values.measure.value;
      }

      await updateTender(tender!.id, {
        ...toUpdateData,
        requirements: values.requirements!.join(','),
        deliveryDate: values.deliveryDate!.toString(),
        endDate: values.endDate!.toString(),
        katoCode: values.kato?.code,
        okedCode: values.oked?.code,
      });

      showNotification({
        variant: 'success',
        message: 'Данные объявления успешно обновлены',
      });

      setPending(false);

      navigate(`/profile/tender/${tender!.id}`);
    } catch (e) {
      showNotification({
        variant: 'error',
        message: 'Не удалось обновить данные объявления',
      });

      setPending(false);
    }
  }, [validate, t, values, showNotification, navigate, tender]);

  const onChangeFiles = useCallback(
    async (files: File[]) => {
      try {
        if (!tender) return null;

        const response = await uploadTenderFiles(tender!.id, files, 'filesIds');
        setTender({ ...tender, filesIds: response.filesIds });
      } catch (e) {}
    },
    [tender]
  );

  const onDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        if (!tender) return false;

        const files = tender
          .filesIds!.split(',')
          .filter((item) => item !== fileId)
          .join(',');

        setTender({ ...tender, filesIds: files });

        await deleteTenderFile(tender.id, fileId);

        return true;
      } catch (e) {
        return false;
      }
    },
    [tender]
  );

  const tenderImages = useMemo(() => {
    if (!tender?.imagesIds) {
      return '';
    }

    return tender?.imagesIds;
  }, [tender?.imagesIds]);

  const leftContent = useMemo(
    () => (
      <div className="w-full max-w-[400px] mx-auto">
        <ProfileAvatar
          avatarFiles={tenderImages}
          editable={true}
          onSubmit={onChangeAvatar}
          customPlaceholder={placeholderImage}
          size={400}
          borderRadius={15}
          className="w-full"
        />
      </div>
    ),
    [onChangeAvatar, tenderImages]
  );

  const rightContent = useMemo(() => {
    if (!tender) {
      return null;
    }

    return (
      <TenderForm
        tender={tender}
        defaultExpanded={true}
        tenderCategory={tender.category}
        onSubmit={onChange}
        edit={true}
        setValues={onChangeTenderForm}
        errors={errors}
      />
    );
  }, [tender, onChange, onChangeTenderForm, errors]);

  const tabLabels = useMemo(() => {
    const returnTabLabels = ['Общая информация'];
    returnTabLabels.push('Документы и Файлы');

    returnTabLabels.push('Отклики');

    return returnTabLabels;
  }, []);

  const tabContent = useMemo<ReactNode[]>(() => {
    if (!tender) {
      return [];
    }

    const content = [
      <div className="flex-grow flex-shrink grid gap-4 auto-rows-min">
        <Grid>
          <TextField
            value={requirement}
            onChange={onChangeRequirement}
            label="требование"
            placeholder="Введите наименование требования. Например, техосмотр не позднее октября"
            error={!!errors.description}
            helperText={errors.description}
            endIcons={[<IconSendMessage onClick={onClickAddRequirement} />]}
            onSendMessage={onClickAddRequirement}
          />
          <div className="flex flex-wrap -ml-4 -mt-4">
            {requirements.map((requirement, requirementIndex) => (
              <div
                key={requirementIndex.toString()}
                className="bg-gray-100 rounded-lg h-12 flex items-center px-4 box-border mt-4 ml-4"
              >
                <p className="font-normal text-base leading-6 tracking-wide text-gray-900 max-w-[500px] truncate mr-2 mb-0">
                  {requirement}
                </p>
                <IconClear
                  onClick={onClickDeleteRequirement(requirementIndex)}
                  className={'form-tender__clearBtn'}
                />
              </div>
            ))}
          </div>
        </Grid>
      </div>,
    ];

    content.push(
      <FilePicker
        loadableFiles={!!tender.filesIds ? tender.filesIds.split(',') : []}
        onChange={onChangeFiles}
        editable={true}
        onDelete={onDeleteFile}
      />
    );
    content.push(<Replies replies={tender.replies} />);

    return [...content];
  }, [
    errors.description,
    onChangeFiles,
    onChangeRequirement,
    onClickAddRequirement,
    onClickDeleteRequirement,
    onDeleteFile,
    requirement,
    requirements,
    tender,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  if (pending) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!tender || error) {
    return null;
  }

  return (
    <div className="grid gap-4 pb-4">
      <LayoutProfile
        pending={pending}
        toolbarProps={{
          title: 'Данные объявления',
        }}
        leftContent={leftContent}
        rightContent={rightContent}
      >
        <Paper>
          <div className="grid gap-4">
            <Tabs labels={tabLabels}>
              {tabContent.map((elem, index) => {
                return <Fragment key={index}>{elem}</Fragment>;
              })}
            </Tabs>
          </div>
        </Paper>
        {/*   <TenderInfo
          tender={tender}
          viewReplies={true}
          actions={actionButtons}
          editable={true}
        />
        <ModalConfirm
          textSuccess={'Успешно удален!'}
          onAccept={onClickDelete}
          title={'Вы уверены, что хотите удалить?'}
          visible={deleteModalVisible}
          onClose={onCloseDeleteModal}
        />*/}
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 mt-4">
          <Button
            text="Отмена"
            color={'default'}
            onClick={onClickCancel}
            className="w-full sm:w-auto"
          />
          <Button
            text="Сохранить"
            icon={IconCheckCircle16}
            onClick={onClickSave}
            loading={pending}
            disabled={pending}
            className="w-full sm:w-auto"
          />
        </div>
      </LayoutProfile>
    </div>
  );
}

export default memo(PageProfileTenderEdit);
